import { useEffect, useState } from "react";
import { Button } from "@/ui/button";
import { Modal } from "@/ui/modals";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import CopyRow from "./copy-row";
import { MinMaxInput, UnitAccordion } from "@/ui/min-max-input";
import Input from "@/ui/input";
import SolanaLogoImage from "../../ui/icon/svg/solana-logo.svg";
import { IconComponent } from "@/ui/icon";

import { useWallet } from "@/providers/wallet";
import { useBotUpdateCopyTradeMutation } from "@/api/bot/bot-update-copy-trade";
import { useCopyTradeWallets } from "@/hooks/use-copy-trade-data";
import WalletSelector from "../copy-trade-controls/wallet-selector";
import {
  formatNativeAmountForDisplay,
  formatNativeCurrency,
} from "@/utils/currency";
import { useBotDeleteCopyTradeMutation } from "@/api/bot/bot-delete-copy-trade";

// Type definitions
type CopyMethod = "amount" | "percentage";
type Status = "active" | "paused";

interface AutoSlippageState {
  enabled: boolean;
  value: string;
}

interface RangeState {
  min?: number;
  max?: number;
}

interface CopyTradeFormState {
  nickname: string;
  copyMethod: CopyMethod;
  copyAmount: string;
  autoSlippage: AutoSlippageState;
  copySells: boolean;
  duplicateBuys: boolean;
  tokenRenounced?: boolean;
  tokenTotalValue: RangeState;
  tokenLiquidity: RangeState;
  tokenMarketCap: RangeState;
  tokenMaxTax: RangeState;
  autoSell: boolean;
  status: Status;
  error: boolean;
  errorMessage: string;
}

// Initial state
const initialFormState: CopyTradeFormState = {
  nickname: "",
  copyMethod: "amount",
  copyAmount: "",
  autoSlippage: {
    enabled: true,
    value: "",
  },
  copySells: true,
  duplicateBuys: false,
  tokenRenounced: undefined,
  tokenTotalValue: {
    min: undefined,
    max: undefined,
  },
  tokenLiquidity: {
    min: undefined,
    max: undefined,
  },
  tokenMarketCap: {
    min: undefined,
    max: undefined,
  },
  tokenMaxTax: {
    min: undefined,
    max: undefined,
  },
  autoSell: true,
  status: "active",
  error: false,
  errorMessage: "",
};

// Safe utility functions
const safeString = (value: any): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const safeNumber = (value: any): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? undefined : num;
};

const safeBoolean = (value: any, defaultValue: boolean = false): boolean => {
  if (typeof value === "boolean") return value;
  if (value === null || value === undefined) return defaultValue;
  return Boolean(value);
};

// const safeCopyMethod = (value: any): CopyMethod => {
//   return value === "percentage" ? "percentage" : "amount";
// };

const safeStatus = (value: any): Status => {
  return value === false ? "paused" : "active";
};

// Safe formatters with error handling
const safeFormatNativeAmountForDisplay = (
  amount: any,
  chainId: any
): string => {
  try {
    if (!amount || isNaN(Number(amount))) return "";
    return formatNativeAmountForDisplay(amount, chainId) || "";
  } catch (error) {
    console.warn("Error formatting native amount for display:", error);
    return "";
  }
};

const safeFormatNativeCurrency = (amount: string, chainId: any): string => {
  try {
    if (!amount || isNaN(Number(amount))) return "0";
    return formatNativeCurrency(amount, chainId) || "0";
  } catch (error) {
    console.warn("Error formatting native currency:", error);
    return "0";
  }
};

const RawCopyTradeModal = ({
  walletAddress,
  isOpen,
  setIsOpen,
}: {
  walletAddress: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}): JSX.Element => {
  const [walletData, setWalletData] = useState<any>(null);
  const [lastWalletAddress, setLastWalletAddress] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  // Safe hook calls with error boundaries
  const { wallets: filteredWallets = [], refreshWallets = () => {} } =
    useCopyTradeWallets() || {};

  const [formState, setFormState] =
    useState<CopyTradeFormState>(initialFormState);

  // Safe wallet context access
  const walletContext = useWallet();
  const {
    userId = "",
    currentWallet = null,
    appPubKey = "",
  } = walletContext || {};

  // Safe mutation hooks
  const updateMutation = useBotUpdateCopyTradeMutation();
  const deleteMutation = useBotDeleteCopyTradeMutation();

  const { mutate: mutateUpdate = () => {} } = updateMutation || {};
  const { mutate: mutateDelete = () => {} } = deleteMutation || {};

  // Enhanced wallet data processing with comprehensive guards
  useEffect(() => {
    if (!isOpen || !walletAddress || !Array.isArray(filteredWallets)) {
      return;
    }

    try {
      setIsLoading(true);

      const selectedWallet: any = filteredWallets.find(
        (wallet) => wallet?.address === walletAddress
      );

      if (selectedWallet) {
        // Safe copyAmount calculation with multiple fallbacks
        let copyAmount = "";
        try {
          if (
            selectedWallet.isBuyWithFixedAmount &&
            selectedWallet.amount &&
            !isNaN(Number(selectedWallet.amount))
          ) {
            copyAmount = safeFormatNativeAmountForDisplay(
              selectedWallet.amount,
              selectedWallet?.sourceWallet?.chainId
            );
          } else if (selectedWallet.amountPercent) {
            copyAmount = safeString(selectedWallet.amountPercent);
          }
        } catch (error) {
          console.warn("Error calculating copy amount:", error);
          copyAmount = "";
        }

        setWalletData(selectedWallet);
        setFormState((prev) => ({
          ...prev,
          nickname: safeString(selectedWallet.nickname),
          copyMethod: selectedWallet.isBuyWithFixedAmount
            ? "amount"
            : "percentage",
          copyAmount,
          autoSlippage: {
            enabled: safeBoolean(selectedWallet.isAutoSlippage, false),
            value: safeString(selectedWallet.slippage),
          },
          copySells: safeBoolean(selectedWallet.copySell, true),
          duplicateBuys: safeBoolean(selectedWallet.isDuplicateBuy, false),
          tokenRenounced: selectedWallet.tokenRenounced,
          tokenTotalValue: {
            min: safeNumber(selectedWallet.minTotalVolume),
            max: safeNumber(selectedWallet.maxTotalVolume),
          },
          tokenLiquidity: {
            min: safeNumber(selectedWallet.tokenMinLiquidity),
            max: safeNumber(selectedWallet.tokenMaxLiquidity),
          },
          tokenMarketCap: {
            min: safeNumber(selectedWallet.tokenMinMC),
            max: safeNumber(selectedWallet.tokenMaxMC),
          },
          tokenMaxTax: {
            min: safeNumber(selectedWallet.tokenMaxSellTax),
            max: safeNumber(selectedWallet.tokenMaxBuyTax),
          },
          autoSell: safeBoolean(selectedWallet.autoSell, true),
          status: safeStatus(selectedWallet.isOn),
          error: false,
          errorMessage: "",
        }));
      } else {
        setWalletData(null);
        setFormState(initialFormState);
      }

      setHasInitialized(true);
    } catch (error) {
      console.error("Error processing wallet data:", error);
      setWalletData(null);
      setFormState({
        ...initialFormState,
        error: true,
        errorMessage: "Error loading wallet data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [filteredWallets, walletAddress, isOpen]);

  // Safe input handlers with error boundaries
  const handleInputChange = (
    field: keyof CopyTradeFormState,
    value: string
  ) => {
    try {
      setFormState((prev) => ({
        ...prev,
        error: false,
        errorMessage: "",
        [field]: value,
      }));
    } catch (error) {
      console.error("Error updating input:", error);
    }
  };

  const handleToggle =
    <K extends keyof CopyTradeFormState>(
      field: K,
      value: CopyTradeFormState[K]
    ) =>
    (): void => {
      try {
        setFormState((prev) => ({
          ...prev,
          error: false,
          errorMessage: "",
          [field]: value,
        }));
      } catch (error) {
        console.error("Error toggling field:", error);
      }
    };

  const handleNestedChange = <
    K extends keyof CopyTradeFormState,
    N extends keyof CopyTradeFormState[K],
  >(
    parent: K,
    field: N,
    value: string
  ) => {
    try {
      setFormState((prev) => ({
        ...prev,
        error: false,
        errorMessage: "",
        [parent]: {
          ...(typeof prev[parent] === "object" && prev[parent] !== null
            ? prev[parent]
            : {}),
          [field]: value,
        } as CopyTradeFormState[K],
      }));
    } catch (error) {
      console.error("Error updating nested field:", error);
    }
  };

  const handleNestedToggle =
    <K extends keyof CopyTradeFormState, N extends keyof CopyTradeFormState[K]>(
      parent: K,
      field: N,
      value: any
    ) =>
    (): void => {
      try {
        setFormState((prev) => ({
          ...prev,
          error: false,
          errorMessage: "",
          [parent]: {
            ...(typeof prev[parent] === "object" && prev[parent] !== null
              ? prev[parent]
              : {}),
            [field]: value,
          } as CopyTradeFormState[K],
        }));
      } catch (error) {
        console.error("Error toggling nested field:", error);
      }
    };

  const handleRangeChange =
    (
      field: keyof Pick<
        CopyTradeFormState,
        "tokenTotalValue" | "tokenLiquidity" | "tokenMarketCap" | "tokenMaxTax"
      >,
      type: keyof RangeState
    ) =>
    (value: number): void => {
      try {
        setFormState((prev) => ({
          ...prev,
          error: false,
          errorMessage: "",
          [field]: {
            ...(typeof prev[field] === "object" && prev[field] !== null
              ? prev[field]
              : {}),
            [type]: value,
          } as RangeState,
        }));
      } catch (error) {
        console.error("Error updating range:", error);
      }
    };

  // Enhanced form validation
  const validateForm = (): boolean => {
    try {
      if (!walletAddress || walletAddress.length === 0) {
        setFormState((prev) => ({
          ...prev,
          error: true,
          errorMessage: "Wallet address is required",
        }));
        return false;
      }

      if (formState.copyMethod === "amount") {
        const amount = Number(formState.copyAmount);
        if (isNaN(amount) || amount <= 0) {
          setFormState((prev) => ({
            ...prev,
            error: true,
            errorMessage: "Please enter a valid amount greater than 0",
          }));
          return false;
        }
      } else if (formState.copyMethod === "percentage") {
        const percentValue = Number(formState.copyAmount);
        if (isNaN(percentValue) || percentValue <= 0 || percentValue > 100) {
          setFormState((prev) => ({
            ...prev,
            error: true,
            errorMessage: "Please enter a valid percentage between 1-100",
          }));
          return false;
        }
      }

      if (!formState.autoSlippage.enabled && formState.autoSlippage.value) {
        const slippage = Number(formState.autoSlippage.value);
        if (isNaN(slippage) || slippage < 0 || slippage > 100) {
          setFormState((prev) => ({
            ...prev,
            error: true,
            errorMessage: "Please enter a valid slippage value (0-100%)",
          }));
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error validating form:", error);
      setFormState((prev) => ({
        ...prev,
        error: true,
        errorMessage: "Validation error occurred",
      }));
      return false;
    }
  };

  // Safe form submission
  const handleSubmit = (): void => {
    try {
      resetError();

      if (!validateForm()) {
        return;
      }

      if (!currentWallet?.botChainId || !currentWallet?.address) {
        setFormState((prev) => ({
          ...prev,
          error: true,
          errorMessage: "Current wallet not properly configured",
        }));
        return;
      }

      const apiParams = {
        chainId: currentWallet.botChainId,
        walletAddress: currentWallet.address,
        copyWallet: walletAddress,
        userId: userId,
        nickname: formState.nickname,
        isAutoSlippage: formState.autoSlippage.enabled,
        slippage: !formState.autoSlippage.enabled
          ? Number(formState.autoSlippage.value) || undefined
          : undefined,
        isBuyWithFixedAmount: formState.copyMethod === "amount",
        buyAmount:
          formState.copyMethod === "amount"
            ? safeFormatNativeCurrency(
                formState.copyAmount || "0",
                currentWallet.botChainId
              )
            : undefined,
        buyAmountPercent:
          formState.copyMethod === "percentage"
            ? Number(formState.copyAmount) || undefined
            : undefined,
        isCopySells: formState.copySells,
        isDuplicateBuys: formState.duplicateBuys,
        isTokenRenounced: formState.tokenRenounced,
        minTotalVolume: formState.tokenTotalValue?.min,
        maxTotalVolume: formState.tokenTotalValue?.max,
        minLiquidity: formState.tokenLiquidity?.min,
        maxLiquidity: formState.tokenLiquidity?.max,
        minMC: formState.tokenMarketCap?.min,
        maxMC: formState.tokenMarketCap?.max,
        minTax: formState.tokenMaxTax?.min,
        maxTax: formState.tokenMaxTax?.max,
        isAutoSell: formState.autoSell,
        isOn: formState.status === "active",
        isDelete: false,
        appPubKey,
      };

      mutateUpdate(apiParams);

      setTimeout(() => {
        try {
          refreshWallets();
        } catch (error) {
          console.warn("Error refreshing wallets:", error);
        }
      }, 1000);

      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      setFormState((prev) => ({
        ...prev,
        error: true,
        errorMessage: "Failed to save changes. Please try again.",
      }));
    }
  };

  // Safe delete handler
  const handleDelete = (): void => {
    try {
      if (!currentWallet?.botChainId || !currentWallet?.address) {
        setFormState((prev) => ({
          ...prev,
          error: true,
          errorMessage: "Current wallet not properly configured",
        }));
        return;
      }

      const apiParams = {
        chainId: currentWallet.botChainId,
        walletAddress: currentWallet.address,
        copyWallet: walletAddress,
        userId: userId,
        appPubKey,
      };

      mutateDelete(apiParams);

      setTimeout(() => {
        try {
          refreshWallets();
        } catch (error) {
          console.warn("Error refreshing wallets:", error);
        }
      }, 1000);

      setIsOpen(false);
    } catch (error) {
      console.error("Error deleting copy trade:", error);
      setFormState((prev) => ({
        ...prev,
        error: true,
        errorMessage: "Failed to delete. Please try again.",
      }));
    }
  };

  const resetForm = (): void => {
    try {
      setFormState(initialFormState);
      setHasInitialized(false);
    } catch (error) {
      console.error("Error resetting form:", error);
    }
  };

  const handleCancel = (): void => {
    try {
      resetForm();
      setIsOpen(false);
    } catch (error) {
      console.error("Error canceling:", error);
      setIsOpen(false); // Always try to close even if reset fails
    }
  };

  const resetError = (): void => {
    try {
      setFormState((prev) => ({
        ...prev,
        error: false,
        errorMessage: "",
      }));
    } catch (error) {
      console.error("Error resetting error state:", error);
    }
  };

  // Safe wallet refresh effect
  useEffect(() => {
    try {
      if (currentWallet?.address !== lastWalletAddress) {
        setLastWalletAddress(currentWallet?.address || "");
        refreshWallets();
      }
    } catch (error) {
      console.error("Error in wallet refresh effect:", error);
    }
  }, [currentWallet?.address, lastWalletAddress, refreshWallets]);

  // // Safe chain detection
  // const getChainIcon = (walletData: any): "ethereum" | "solanaLogo" => {
  //   try {
  //     return walletData?.chain === "ethereum" ? "ethereum" : "solanaLogo";
  //   } catch (error) {
  //     return "solanaLogo"; // Default fallback
  //   }
  // };

  // Show loading state if still initializing
  if (isLoading && !hasInitialized) {
    return (
      <Modal
        opened={isOpen}
        onClose={handleCancel}
        className="flex flex-col pb-[0.9375rem] items-center w-[39.125rem] !gap-0 py-[0.9375rem] px-[0.6250rem]"
        size="auto"
      >
        <div className="flex items-center justify-center h-32">
          <p className="text-white">Loading...</p>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={handleCancel}
        className="flex flex-col items-center w-[39.125rem] !gap-0 py-[0.9375rem] px-[10px]"
        size="auto"
      >
        <div className="w-full flex flex-row relative justify-center items-center">
          <div className="absolute left-0">
            <WalletSelector
              isAllSelected={false}
              setIsAllSelected={() => {}}
              hasAll={false}
            />
          </div>
          <p className="text-[0.8rem] text-white font-semibold text-center">
            Copy Trade
          </p>
          <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center absolute right-0">
            <IconComponent
              name="x"
              className="w-[0.55rem] h-[0.55rem] cursor-pointer"
              onClick={handleCancel}
            />
          </div>
        </div>
        <div className="mt-[0.625rem] h-[1.5625rem] w-full  bg-black flex items-center">
          <p className="text-xs font-semibold text-white ">Wallet</p>
        </div>
        <div className="w-full text-white  py-[0.438rem]">
          <div className="w-full flex flex-row items-center justify-start gap-[0.3125rem]">
            <div className="flex items-center rounded-full justify-center w-[1.1875rem] h-[1.1875rem] bg-black">
              <div
                className={`${!walletData ? "bg-[#717BF7]" : walletData?.isOn === true ? "bg-green" : "bg-yellow-300"} w-[58.86%] h-[58.86%] rounded-full`}
              ></div>
            </div>
            <div className="w-[1.1875rem] h-[1.1875rem] z-0 rounded-full flex justify-center items-center bg-black">
              <img
                src={SolanaLogoImage}
                alt="wallet-icon"
                className="w-[1.1875rem] h-[1.1875rem] rounded-full"
              />
            </div>
            <p className="text-[0.75rem] text-white font-semibold">
              {walletAddress || "Unknown Address"}
            </p>
          </div>
        </div>
        <div className="w-full h-full flex flex-col justify-center items-center gap-[0.375rem] text-[0.75rem] text-white">
          <CopyRow title="Nickname">
            <Input
              placeholder="Nickname"
              className="min-w-full"
              value={formState.nickname}
              onChange={(e) => handleInputChange("nickname", e)}
            />
          </CopyRow>
          <CopyRow title="Copy by">
            <div className="w-[100%] flex flex-row gap-[0.3rem]">
              <Button
                radius="normal"
                height="h-full"
                active={formState.copyMethod === "amount"}
                title="Amount"
                onClick={handleToggle("copyMethod", "amount")}
                className="text-[0.75rem] w-full"
              />
              <Button
                radius="normal"
                height="h-full"
                active={formState.copyMethod === "percentage"}
                title="%"
                className="text-[0.75rem] w-full"
                onClick={handleToggle("copyMethod", "percentage")}
              />
            </div>
            <div className="w-full group">
              <Input
                leftSide={
                  <GradientBorderRadius
                    radius="right"
                    gradient={
                      formState.copyAmount && formState.copyAmount !== ""
                        ? "reversed-100"
                        : "default-border"
                    }
                    hover="input-50-left"
                    className="h-full w-[1.375rem]"
                  >
                    <div className="w-[1.375rem] h-full  flex items-center justify-center pl-[0.0625rem]">
                      <IconComponent
                        name="solanaLogo"
                        className="h-[1rem] w-[1rem]"
                      />
                    </div>
                  </GradientBorderRadius>
                }
                placeholder={
                  formState.copyMethod === "amount" ? "Amount" : "Percentage"
                }
                value={formState.copyAmount}
                onChange={(e) => handleInputChange("copyAmount", e)}
              />
            </div>
          </CopyRow>
          <CopyRow title="Auto Slippage">
            <div className="w-[100%] flex flex-row gap-[0.3rem]">
              <Button
                radius="normal"
                height="h-full"
                active={formState.autoSlippage.enabled}
                title="Yes"
                onClick={handleNestedToggle("autoSlippage", "enabled", true)}
                className="text-[0.75rem] w-full"
              />
              <Button
                radius="normal"
                height="h-full"
                active={!formState.autoSlippage.enabled}
                title="No"
                className="text-[0.75rem] w-full"
                onClick={handleNestedToggle("autoSlippage", "enabled", false)}
              />
            </div>
            <div className="w-full group">
              <Input
                rightSide={
                  <GradientBorderRadius
                    radius="left"
                    gradient={
                      formState.autoSlippage.value &&
                      formState.autoSlippage.value !== ""
                        ? "reversed-100"
                        : "default-border"
                    }
                    hover="input-50-right"
                    className="h-full w-[1.375rem]"
                  >
                    <div className="w-[1.375rem] h-full  flex items-center justify-center pl-[0.0625rem]">
                      <UnitAccordion text="%" />
                    </div>
                  </GradientBorderRadius>
                }
                placeholder="Amount"
                value={formState.autoSlippage.value}
                onChange={(e) => handleNestedChange("autoSlippage", "value", e)}
              />
            </div>
          </CopyRow>
          <CopyRow title="Copy Sells">
            <div className="w-[100%] flex flex-row gap-[0.3rem]">
              <Button
                radius="normal"
                height="h-full"
                active={formState.copySells}
                title="Yes"
                onClick={handleToggle("copySells", true)}
                className="text-[0.75rem] w-full"
              />
              <Button
                radius="normal"
                height="h-full"
                className="text-[0.75rem] w-full"
                active={!formState.copySells}
                title="No"
                onClick={handleToggle("copySells", false)}
              />
            </div>
          </CopyRow>
          <CopyRow title="Duplicate buys">
            <div className="w-[100%] flex flex-row gap-[0.3rem]">
              <Button
                radius="normal"
                height="h-full"
                active={formState.duplicateBuys}
                title="Yes"
                onClick={handleToggle("duplicateBuys", true)}
                className="text-[0.75rem] w-full"
              />
              <Button
                radius="normal"
                height="h-full"
                active={!formState.duplicateBuys}
                title="No"
                onClick={handleToggle("duplicateBuys", false)}
                className="text-[0.75rem] w-full"
              />
            </div>
          </CopyRow>
          <CopyRow title="Token Volume">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formState.tokenTotalValue.min}
              maxValue={formState.tokenTotalValue.max}
              onMinChange={handleRangeChange("tokenTotalValue", "min")}
              onMaxChange={handleRangeChange("tokenTotalValue", "max")}
            />
          </CopyRow>
          <CopyRow title="Token Liqudity">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formState.tokenLiquidity.min}
              maxValue={formState.tokenLiquidity.max}
              onMinChange={handleRangeChange("tokenLiquidity", "min")}
              onMaxChange={handleRangeChange("tokenLiquidity", "max")}
            />
          </CopyRow>
          <CopyRow title="Token Market Cap">
            <MinMaxInput
              unitSide="left"
              text="$"
              layout="row"
              minValue={formState.tokenMarketCap.min}
              maxValue={formState.tokenMarketCap.max}
              onMinChange={handleRangeChange("tokenMarketCap", "min")}
              onMaxChange={handleRangeChange("tokenMarketCap", "max")}
            />
          </CopyRow>
          <CopyRow title="Auto Sell">
            <div className="w-[100%] flex flex-row gap-[0.3rem]">
              <Button
                radius="normal"
                height="h-full"
                active={formState.autoSell}
                title="Yes"
                onClick={handleToggle("autoSell", true)}
                className="text-[0.75rem] w-full"
              />
              <Button
                radius="normal"
                height="h-full"
                active={!formState.autoSell}
                title="No"
                onClick={handleToggle("autoSell", false)}
                className="text-[0.75rem] w-full"
              />
            </div>
          </CopyRow>
          <CopyRow title="Status">
            <div className="w-[100%] flex flex-row gap-[0.3rem]">
              <Button
                radius="normal"
                height="h-full"
                active={formState.status === "active"}
                title="Active"
                onClick={handleToggle("status", "active")}
                className="text-[0.75rem] w-full"
              />
              <Button
                radius="normal"
                height="h-full"
                active={formState.status === "paused"}
                title="Paused"
                onClick={handleToggle("status", "paused")}
                className="text-[0.75rem] w-full"
              />
            </div>
          </CopyRow>
        </div>
        <Modal.Footer>
          <div className="mt-[0.938rem] w-full flex flex-col items-center justify-start gap-[0.3125rem]">
            {formState.error && (
              <p className="text-rose-600 text-xs font-semibold">
                {formState.errorMessage}
              </p>
            )}
            <div className="w-full flex flex-row gap-[0.3125rem] justify-center items-center">
              {!walletData ? (
                <>
                  <Button
                    title="Save"
                    active={false}
                    className="w-[9.4375rem] font-semibold text-xs"
                    onClick={handleSubmit}
                  />
                  <Button
                    title="Cancel"
                    active={false}
                    className="w-[9.4375rem] font-semibold text-xs"
                    onClick={handleCancel}
                  />
                </>
              ) : (
                <>
                  <Button
                    title="Done"
                    active={false}
                    className="w-[9.4375rem] font-semibold text-xs"
                    onClick={handleSubmit}
                  />

                  <Button
                    title="Delete"
                    active={false}
                    className="w-[9.4375rem] font-semibold text-xs"
                    onClick={handleDelete}
                  />
                </>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RawCopyTradeModal;
